import { ThemeProvider, type Theme } from '@aws-amplify/ui-react';
import { FaceLivenessDetector } from '@aws-amplify/ui-react-liveness';
import Box from '@mui/material/Box';
import { COLORS } from '@/theme';

type Props = {
  sessionId: string;
  region: string;
  /** O desafio terminou no browser; o BFF valida o resultado na AWS. */
  onAnalysisComplete: () => Promise<void>;
  onError: (error: unknown) => void;
  onUserCancel: () => void;
};

/**
 * Tema do detector na paleta Identila: violet como ação, lime como marcador,
 * Outfit como fonte. O Amplify lê estes tokens como CSS variables, então o
 * oval, as dicas, o indicador de aproximação e o loader seguem a marca.
 */
const livenessTheme: Theme = {
  name: 'identila-liveness',
  tokens: {
    fonts: { default: { variable: { value: '"Outfit", system-ui, sans-serif' }, static: { value: '"Outfit", system-ui, sans-serif' } } },
    colors: {
      primary: {
        10: { value: '#F3EAFB' },
        20: { value: '#E3CDF5' },
        40: { value: '#C39DE7' },
        60: { value: '#A66BDC' },
        80: { value: COLORS.violet },
        90: { value: '#6E2FAA' },
        100: { value: COLORS.plum },
      },
      font: { primary: { value: COLORS.text }, secondary: { value: '#4C4F4D' }, inverse: { value: '#FFFFFF' } },
      background: { primary: { value: '#FFFFFF' }, secondary: { value: COLORS.background } },
      border: { primary: { value: COLORS.line } },
    },
    radii: { small: { value: '8px' }, medium: { value: '16px' }, large: { value: '24px' } },
    components: {
      liveness: { cameraModule: { backgroundColor: { value: COLORS.plum } } },
    },
  },
};

const DISPLAY_TEXT = {
  hintCenterFaceText: 'Centralize o rosto no oval',
  hintCenterFaceInstructionText: 'Posicione o rosto dentro do oval',
  hintFaceOffCenterText: 'Rosto fora do centro',
  hintMoveFaceFrontOfCameraText: 'Fique de frente para a câmera',
  hintTooManyFacesText: 'Só uma pessoa na câmera',
  hintFaceDetectedText: 'Rosto detectado',
  hintCanNotIdentifyText: 'Não encontramos seu rosto',
  hintTooCloseText: 'Afaste um pouco',
  hintTooFarText: 'Aproxime um pouco',
  hintConnectingText: 'Conectando…',
  hintVerifyingText: 'Verificando…',
  hintCheckCompleteText: 'Prova de vida concluída',
  hintHoldFaceForFreshnessText: 'Fique parado',
  hintMatchIndicatorText: '50% concluído. Continue aproximando.',
  hintIlluminationTooBrightText: 'Muita luz: vá para um lugar mais escuro',
  hintIlluminationTooDarkText: 'Pouca luz: vá para um lugar mais claro',
  hintIlluminationNormalText: 'Iluminação boa',
  waitingCameraPermissionText: 'Aguardando permissão da câmera…',
  cameraNotFoundHeadingText: 'Câmera não encontrada',
  cameraNotFoundMessageText: 'Verifique se há uma câmera conectada e se o navegador tem permissão.',
  cameraMinSpecificationsHeadingText: 'Câmera abaixo do mínimo',
  cameraMinSpecificationsMessageText: 'A câmera precisa de 320×240 a 15 quadros por segundo.',
  retryCameraPermissionsText: 'Tentar novamente',
  recordingIndicatorText: 'Gravando',
  cancelLivenessCheckText: 'Cancelar',
};

/**
 * Prova de vida AWS Rekognition (Amplify). A tela inicial padrão da Amplify
 * fica desligada — a preparação é nossa (`SelfieIntro`) — e o modal de erro
 * padrão não renderiza: o erro chega por `onError` e a página mostra o
 * cartão da marca com a causa traduzida.
 */
export function LivenessCapture({ sessionId, region, onAnalysisComplete, onError, onUserCancel }: Props) {
  return (
    <ThemeProvider theme={livenessTheme}>
      <Box className="liveness-wrap">
        <FaceLivenessDetector
          sessionId={sessionId}
          region={region}
          disableStartScreen
          onAnalysisComplete={onAnalysisComplete}
          onError={onError}
          onUserCancel={onUserCancel}
          displayText={DISPLAY_TEXT}
          components={{ CancelButton: null, ErrorView: () => null }}
        />
      </Box>
    </ThemeProvider>
  );
}
